import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HeroSection from '../components/HeroSection';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const industries = [
  {
    image: '/images/offline/restaurant-service-modern.jpg',
    alt: 'Modern American restaurant with staff and diners',
    name: 'Restaurants & Food Service',
    desc: 'Bridge slow seasons, expand your menu, buy new equipment, or open a second location.',
  },
  {
    image: 'https://wp.sfdcdigital.com/en-au/wp-content/uploads/sites/12/2025/04/what-is-retail-featured-image.jpg?resize=1536,1024',
    alt: 'Modern retail store interior',
    name: 'Retail',
    desc: 'Stock up before peak season, renovate your storefront, or launch an ecommerce operation.',
  },
  {
    image: 'https://img.magnific.com/free-photo/civil-engineer-construction-worker-architects-wearing-hardhats-safety-vests-are-working-together-construction-site-building-home-cooperation-teamwork-concept_640221-172.jpg?semt=ais_hybrid&w=740&q=80',
    alt: 'Construction workers collaborating on a building site',
    name: 'Construction',
    desc: 'Cover payroll, purchase materials, and keep projects on schedule while waiting on receivables.',
  },
  {
    image: 'https://imageio.forbes.com/specials-images/dam/imageserve/1053783710/0x0.jpg?format=jpg&height=900&width=1600&fit=bounds',
    alt: 'Medical professionals in a modern healthcare setting',
    name: 'Medical & Dental',
    desc: 'Buy new equipment, hire support staff, or expand your practice with fast, flexible capital.',
  },
  {
    image: 'https://cdn.prod.website-files.com/678cca1dba65076a9fd94f2d/678fd16ce5344f8a734d0056_646be3b503e10585e1dab167_624e00c95b576f081c75a271_tekmetric_socialasset_R2_130-min.jpeg',
    alt: 'Automotive repair shop with technicians working on vehicles',
    name: 'Auto Repair & Dealerships',
    desc: 'Upgrade shop equipment, manage inventory, or expand your service bays.',
  },
  {
    image: '/images/offline/beauty-salon.jpg',
    alt: 'Beauty salon with stylist and client',
    name: 'Beauty & Wellness',
    desc: 'Open a new location, update your salon, or invest in premium products and training.',
  },
  {
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQZOduhlWRVEPSNufXbRmooHFU5hnaynuikBA&s',
    alt: 'Logistics and transportation fleet in motion',
    name: 'Transportation & Logistics',
    desc: 'Expand your fleet, cover fuel, and keep operations moving even between loads.',
  },
  {
    image: '/images/offline/legal-office.jpg',
    alt: 'Professional law office consultation',
    name: 'Legal & Professional Services',
    desc: 'Hire support staff, upgrade technology, or cover operational costs between cases.',
  },
  {
    image: 'https://www.entrepreneur.com/wp-content/uploads/sites/2/2018/05/20150622231001-for-sale-real-estate-home-house.jpeg',
    alt: 'Real estate listing with a home for sale',
    name: 'Real Estate',
    desc: 'Bridge funding for flips, renovations, or agent operations with fast access to capital.',
  },
  {
    image: '/images/offline/tech-team.jpg',
    alt: 'Technology team collaborating in modern office',
    name: 'Technology & SaaS',
    desc: 'Accelerate growth, fund marketing, or hire key engineers without giving up equity.',
  },
  {
    image: 'https://www.miquido.com/wp-content/uploads/2025/05/header-e-business-integration-in-manufacturing-%E2%80%93-2024-statistics.jpg',
    alt: 'Modern manufacturing facility with integrated technology',
    name: 'Manufacturing',
    desc: 'Purchase raw materials, upgrade machinery, or fulfill large orders ahead of payment.',
  },
  {
    image: '/images/offline/education.jpg',
    alt: 'Classroom with teacher and students',
    name: 'Education & Childcare',
    desc: 'Hire staff, improve facilities, and invest in curriculum with flexible financing.',
  },
  {
    image: '/images/offline/agriculture.jpg',
    alt: 'Farmer with successful harvest',
    name: 'Agriculture & Farming',
    desc: 'Cover seed, equipment, and labor costs before harvest season revenue arrives.',
  },
  {
    image: '/images/offline/hotel.jpg',
    alt: 'Modern hotel lobby and entrance',
    name: 'Hospitality & Hotels',
    desc: 'Upgrade guest rooms, hire seasonal staff, and market ahead of your busy season.',
  },
  {
    image: '/images/offline/fitness-gym.jpg',
    alt: 'Modern gym with equipment and members',
    name: 'Fitness & Gyms',
    desc: 'Buy new equipment, build out studio space, or grow your membership marketing.',
  },
  {
    image: '/images/offline/hvac-trades.jpg',
    alt: 'HVAC technician servicing equipment',
    name: 'HVAC & Trades',
    desc: 'Cover equipment, parts inventory, and crew costs before invoices are paid.',
  },
];

export default function Industries() {
  return (
    <main>
      <HeroSection
        tag="Industries We Serve"
        title="Your Industry. Our Capital."
        image="/images/offline/photo-1542744173-8e7e53415bb0.jpg"
      >
        <Link to="/apply" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3.5 rounded-full transition-colors inline-flex items-center gap-2">
          Apply for Funding <ArrowRight size={16} />
        </Link>
      </HeroSection>

      {/* Industry Grid */}
      <section className="page-section bg-white">
        <div className="page-container-wide">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="section-intro-wide text-center">
            <span className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Our Coverage</span>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">We've funded businesses in virtually every corner of the American economy. Here's a sample of the industries we serve.</p>
          </motion.div>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {industries.map((ind, index) => (
              <motion.div key={ind.name} variants={fadeUp} className="group cursor-pointer overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-all hover:border-blue-200 hover:shadow-md">
                <div className="relative h-44 overflow-hidden bg-gray-100 sm:h-48">
                  <img
                    src={ind.image}
                    alt={ind.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-bold text-gray-900 text-sm group-hover:text-blue-600 transition-colors">{ind.name}</h3>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Dark CTA */}
      <section style={{ background: '#111' }} className="page-section-tight">
        <div className="page-container max-w-3xl text-center text-white">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl font-bold text-white mb-4">Don't See Your Industry?</h2>
            <p className="text-white/60 mb-8">We fund many more business types. Reach out to our team and we'll find the right product for you.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/apply" className="cta-primary">Apply for Funding</Link>
              <Link to="/contact" className="border border-white/30 hover:border-white text-white font-semibold px-8 py-3.5 rounded-full transition-colors">Contact Us</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
